import jsPDF from "jspdf";

const BRAND_GREEN = [13, 61, 43]; // hsl(150,80%,20%) ~ #0d3d2b
const TEXT_DARK = [26, 29, 36];
const MUTED = [110, 115, 125];
const BG_PAPER = [247, 244, 238];

/**
 * Generate a branded TaxShift 2026 PDF report.
 * @param {Object} opts
 * @param {string} opts.title - e.g. "2026 Federal Tax Bracket Analysis"
 * @param {string} opts.subtitle - subtitle text
 * @param {Array<{label:string,value:string}>} opts.inputs
 * @param {Array<{label:string,v2025:string,v2026:string,delta?:string}>} opts.rows
 * @param {{label:string,v2025:string,v2026:string}|null} opts.totals
 * @param {string} opts.headline - bottom-line statement
 * @param {string} [opts.filename]
 */
export const generateCalculatorPdf = ({
  title,
  subtitle,
  inputs = [],
  rows = [],
  totals = null,
  headline = "",
  filename = "taxshift-2026-analysis.pdf",
}) => {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Paper background
  doc.setFillColor(...BG_PAPER);
  doc.rect(0, 0, pageW, pageH, "F");

  // Header band
  doc.setFillColor(...TEXT_DARK);
  doc.rect(0, 0, pageW, 90, "F");
  doc.setFillColor(...BRAND_GREEN);
  doc.rect(40, 30, 36, 36, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("times", "bold");
  doc.setFontSize(22);
  doc.text("T", 58, 56, { align: "center" });
  doc.setFontSize(20);
  doc.text("TaxShift 2026", 90, 52);
  doc.setFontSize(9);
  doc.setFont("courier", "normal");
  doc.setTextColor(200, 200, 200);
  doc.text("OBBBA NAVIGATOR · EDITORIAL CALCULATOR SUITE", 90, 68);
  doc.text("taxshift2026.com", pageW - 40, 56, { align: "right" });

  // Title
  let y = 130;
  doc.setTextColor(...TEXT_DARK);
  doc.setFont("times", "bold");
  doc.setFontSize(26);
  doc.text(title, 40, y);
  y += 18;
  if (subtitle) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(11);
    doc.setTextColor(...MUTED);
    doc.text(subtitle, 40, y);
    y += 18;
  }

  // Horizontal rule
  y += 10;
  doc.setDrawColor(...TEXT_DARK);
  doc.setLineWidth(1.5);
  doc.line(40, y, pageW - 40, y);
  y += 24;

  // Inputs
  if (inputs.length) {
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("§ INPUTS", 40, y);
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...TEXT_DARK);
    inputs.forEach((it) => {
      doc.setFont("helvetica", "normal");
      doc.text(it.label, 40, y);
      doc.setFont("courier", "normal");
      doc.text(String(it.value), pageW - 40, y, { align: "right" });
      y += 16;
    });
    y += 10;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(40, y, pageW - 40, y);
    y += 20;
  }

  // Headline
  if (headline) {
    doc.setFillColor(...TEXT_DARK);
    doc.rect(40, y - 16, pageW - 80, 60, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("courier", "normal");
    doc.setFontSize(8);
    doc.text("BOTTOM LINE", 54, y);
    y += 16;
    doc.setFont("times", "bold");
    doc.setFontSize(14);
    const headlineLines = doc.splitTextToSize(headline, pageW - 110);
    doc.text(headlineLines, 54, y);
    y += headlineLines.length * 16 + 16;
  }

  // Comparison table
  doc.setTextColor(...TEXT_DARK);
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.text("§ 2025 vs 2026 COMPARISON", 40, y);
  y += 16;

  // Table header
  doc.setFillColor(...TEXT_DARK);
  doc.rect(40, y - 2, pageW - 80, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("courier", "bold");
  doc.setFontSize(8);
  doc.text("METRIC", 48, y + 12);
  doc.text("2025", pageW / 2 + 40, y + 12, { align: "right" });
  doc.setFillColor(...BRAND_GREEN);
  doc.rect(pageW / 2 + 60, y - 2, pageW / 2 - 100, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.text("2026", pageW - 48, y + 12, { align: "right" });
  y += 28;

  // Table rows
  doc.setTextColor(...TEXT_DARK);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  rows.forEach((r, i) => {
    if (i % 2 === 0) {
      doc.setFillColor(240, 240, 235);
      doc.rect(40, y - 12, pageW - 80, 20, "F");
    }
    doc.setFont("helvetica", "normal");
    doc.text(r.label, 48, y);
    doc.setFont("courier", "normal");
    doc.text(String(r.v2025), pageW / 2 + 40, y, { align: "right" });
    doc.setFont("courier", "bold");
    doc.text(String(r.v2026), pageW - 48, y, { align: "right" });
    y += 20;
  });

  // Totals
  if (totals) {
    doc.setFillColor(...TEXT_DARK);
    doc.rect(40, y - 4, pageW - 80, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    doc.text(totals.label, 48, y + 14);
    doc.setFont("courier", "bold");
    doc.text(String(totals.v2025), pageW / 2 + 40, y + 14, { align: "right" });
    doc.setFillColor(...BRAND_GREEN);
    doc.rect(pageW / 2 + 60, y - 4, pageW / 2 - 100, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.text(String(totals.v2026), pageW - 48, y + 14, { align: "right" });
    y += 36;
  }

  // Footer
  const footerY = pageH - 50;
  doc.setDrawColor(...TEXT_DARK);
  doc.setLineWidth(0.5);
  doc.line(40, footerY - 10, pageW - 40, footerY - 10);
  doc.setFont("courier", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.text(
    "STATUTORY PROJECTION · ILLUSTRATIVE · NOT FINANCIAL ADVICE",
    pageW / 2,
    footerY,
    { align: "center" }
  );
  doc.text(
    `Generated ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · taxshift2026.com`,
    pageW / 2,
    footerY + 12,
    { align: "center" }
  );

  doc.save(filename);
};
